import { useCallback, useEffect, useState } from 'react'

import { usePursuits } from '@/hooks/pursuits/usePursuits'
import { usePursuitScope } from '@/hooks/pursuits/usePursuitScope'
import { usePursuitStages } from '@/hooks/pursuits/usePursuitStages'

import type { UUID } from '@/types/Id'
import type { Pursuit } from '@/types/pursuit/Pursuit'
import type { PursuitStage } from '@/types/pursuit/Stage'

const STORAGE_KEY_PREFIX = 'sweetspot-pursuit-estimated-value-selected-stages'
const STAGE_NAME_STORAGE_KEY_PREFIX =
  'sweetspot-pursuit-estimated-value-selected-stage-names'
const STAGE_SELECTION_MODE_STORAGE_KEY_PREFIX =
  'sweetspot-pursuit-estimated-value-selection-mode'
const MULTI_ORG_SCOPE_KEY = 'multi'
const MULTI_ORG_STAGE_ID_STORAGE_KEY = `${STORAGE_KEY_PREFIX}-all-orgs-overview-by-org`
const MULTI_ORG_STAGE_NAME_STORAGE_KEY = `${STAGE_NAME_STORAGE_KEY_PREFIX}-all-orgs-overview`
const MULTI_ORG_STAGE_SELECTION_MODE_STORAGE_KEY = `${STAGE_SELECTION_MODE_STORAGE_KEY_PREFIX}-all-orgs-overview`

const getStageIdStorageKey = (scopeKey: string) =>
  scopeKey === MULTI_ORG_SCOPE_KEY
    ? MULTI_ORG_STAGE_ID_STORAGE_KEY
    : `${STORAGE_KEY_PREFIX}-${scopeKey}`

const getStageNameStorageKey = (scopeKey: string) =>
  scopeKey === MULTI_ORG_SCOPE_KEY
    ? MULTI_ORG_STAGE_NAME_STORAGE_KEY
    : `${STAGE_NAME_STORAGE_KEY_PREFIX}-${scopeKey}`

const getStageSelectionModeStorageKey = (scopeKey: string) =>
  scopeKey === MULTI_ORG_SCOPE_KEY
    ? MULTI_ORG_STAGE_SELECTION_MODE_STORAGE_KEY
    : `${STAGE_SELECTION_MODE_STORAGE_KEY_PREFIX}-${scopeKey}`

const readStoredStringArray = (key: string): string[] | undefined => {
  const stored = localStorage.getItem(key)
  if (stored === null) return undefined

  const parsed: unknown = JSON.parse(stored)
  return Array.isArray(parsed)
    ? parsed.filter((value): value is string => typeof value === 'string')
    : undefined
}

interface StoredStageSelection {
  stageIds: UUID[]
  stageNames: string[] | null
  hasStoredSelection: boolean
}

const emptyStoredStageSelection: StoredStageSelection = {
  stageIds: [],
  stageNames: null,
  hasStoredSelection: false,
}

const getStoredStageSelection = (scopeKey: string): StoredStageSelection => {
  if (typeof window === 'undefined' || !scopeKey) {
    return emptyStoredStageSelection
  }
  try {
    const stageNames = readStoredStringArray(getStageNameStorageKey(scopeKey))
    const stageIds = readStoredStringArray(getStageIdStorageKey(scopeKey))
    const selectionMode = localStorage.getItem(
      getStageSelectionModeStorageKey(scopeKey),
    )

    if (
      stageNames !== undefined &&
      stageNames.length === 0 &&
      (stageIds?.length ?? 0) === 0 &&
      selectionMode !== 'custom'
    ) {
      return emptyStoredStageSelection
    }

    if (stageNames !== undefined) {
      return {
        stageIds: (stageIds ?? []) as UUID[],
        stageNames,
        hasStoredSelection: true,
      }
    }

    if (stageIds !== undefined) {
      return {
        stageIds: stageIds as UUID[],
        stageNames: null,
        hasStoredSelection: true,
      }
    }
  } catch {
    // Ignore storage errors
  }

  return emptyStoredStageSelection
}

export const setSelectedStageIds = (scopeKey: string, stageIds: UUID[]) => {
  if (typeof window === 'undefined' || !scopeKey) {
    return
  }
  try {
    localStorage.setItem(
      getStageIdStorageKey(scopeKey),
      JSON.stringify(stageIds),
    )
  } catch {
    // Ignore storage errors
  }
}

const setStoredStageSelection = (
  scopeKey: string,
  selection: StoredStageSelection,
) => {
  if (typeof window === 'undefined' || !scopeKey) {
    return
  }
  try {
    localStorage.setItem(
      getStageIdStorageKey(scopeKey),
      JSON.stringify(selection.stageIds),
    )
    localStorage.setItem(
      getStageNameStorageKey(scopeKey),
      JSON.stringify(selection.stageNames ?? []),
    )
    localStorage.setItem(getStageSelectionModeStorageKey(scopeKey), 'custom')
  } catch {
    // Ignore storage errors
  }
}

const defaultSummary = {
  totalValue: 0,
  nullOrZeroCount: 0,
}

interface StageSelectionData {
  stages: PursuitStage[]
  selectedStageIds: UUID[]
  stageStatistics: {
    allStageIds: UUID[]
    allSelected: boolean
  }
}

const defaultStageSelectionData: StageSelectionData = {
  stages: [],
  selectedStageIds: [],
  stageStatistics: {
    allStageIds: [],
    allSelected: false,
  },
}

const getStageNamesForIds = (
  stages: PursuitStage[],
  stageIds: UUID[],
): string[] => {
  if (stageIds.length === 0) return []

  const selectedStageIdSet = new Set(stageIds)
  const selectedStageNames: string[] = []
  const seenStageNames = new Set<string>()

  for (const stage of stages) {
    if (selectedStageIdSet.has(stage.id) && !seenStageNames.has(stage.name)) {
      seenStageNames.add(stage.name)
      selectedStageNames.push(stage.name)
    }
  }

  return selectedStageNames
}

const resolveSelectedStageIds = (
  stages: PursuitStage[],
  selection: StoredStageSelection,
): UUID[] => {
  if (stages.length === 0) return []

  if (!selection.hasStoredSelection) {
    return stages.map((stage) => stage.id)
  }

  const hasStoredStageIds = selection.stageIds.length > 0
  const hasStoredStageNames = (selection.stageNames?.length ?? 0) > 0
  const selectedStageNames = new Set(selection.stageNames ?? [])
  if (hasStoredStageIds) {
    const selectedStageIdSet = new Set(selection.stageIds)
    for (const stage of stages) {
      if (selectedStageIdSet.has(stage.id)) {
        selectedStageNames.add(stage.name)
      }
    }
  }

  if (selectedStageNames.size === 0) {
    return hasStoredStageIds || hasStoredStageNames
      ? stages.map((stage) => stage.id)
      : []
  }

  const normalizedStageIds: UUID[] = []
  for (const stage of stages) {
    if (selectedStageNames.has(stage.name)) {
      normalizedStageIds.push(stage.id)
    }
  }
  return normalizedStageIds.length > 0
    ? normalizedStageIds
    : stages.map((stage) => stage.id)
}

export interface PipelineEstimatedValueData {
  totalValue: number
  nullOrZeroCount: number
  isLoading: boolean
  selectedStageIds: UUID[]
  setSelectedStageIds: (stageIds: UUID[]) => void
  stageStatistics: {
    allStageIds: UUID[]
    allSelected: boolean
  }
  stages: PursuitStage[] | undefined
}

export const usePipelineEstimatedValue = (): PipelineEstimatedValueData => {
  const scope = usePursuitScope()
  const scopeKey = scope.isMultiOrg ? MULTI_ORG_SCOPE_KEY : scope.primaryOrgId

  const [storedSelection, setStoredSelection] = useState<StoredStageSelection>(
    () => getStoredStageSelection(scopeKey),
  )
  const storedStageIds = storedSelection.stageIds
  const storedStageNames = storedSelection.stageNames
  const hasStoredSelection = storedSelection.hasStoredSelection

  const selectStageSelection = useCallback(
    (stages: PursuitStage[]) => {
      const allStageIds = stages.map((stage) => stage.id)
      const selectedStageIds = resolveSelectedStageIds(stages, {
        stageIds: storedStageIds,
        stageNames: storedStageNames,
        hasStoredSelection,
      })
      const selectedStageIdSet = new Set(selectedStageIds)

      return {
        stages,
        selectedStageIds,
        stageStatistics: {
          allStageIds,
          allSelected:
            allStageIds.length > 0 &&
            allStageIds.every((id) => selectedStageIdSet.has(id)),
        },
      }
    },
    [hasStoredSelection, storedStageIds, storedStageNames],
  )

  const {
    data: stageSelection = defaultStageSelectionData,
    isLoading: isStageSelectionLoading,
  } = usePursuitStages({
    orgIds: scope.orgIds,
    dedupe: false,
    selector: selectStageSelection,
  })
  const { selectedStageIds, stageStatistics, stages } = stageSelection

  const selectSummary = useCallback(
    (pursuits: Pursuit[]) => {
      const selectedStageIdSet = new Set(selectedStageIds)
      let totalValue = 0
      let nullOrZeroCount = 0

      for (const pursuit of pursuits) {
        if (pursuit.archived || pursuit.terminal_status === 'no_bid') {
          continue
        }

        const estimatedValue = pursuit.estimated_value ?? 0
        if (estimatedValue === 0) {
          nullOrZeroCount += 1
        }
        if (selectedStageIdSet.has(pursuit.stage.id)) {
          totalValue += estimatedValue
        }
      }

      return { totalValue, nullOrZeroCount }
    },
    [selectedStageIds],
  )

  const { data: summary = defaultSummary, isLoading: isSummaryLoading } =
    usePursuits({
      orgIds: scope.orgIds,
      selector: selectSummary,
    })

  const isLoading = isStageSelectionLoading || isSummaryLoading

  const handleSetSelectedStageIds = useCallback(
    (stageIds: UUID[]) => {
      const stageNames = getStageNamesForIds(stages, stageIds)
      const nextSelection: StoredStageSelection = {
        stageIds: resolveSelectedStageIds(stages, {
          stageIds,
          stageNames,
          hasStoredSelection: true,
        }),
        stageNames,
        hasStoredSelection: true,
      }
      setStoredSelection(nextSelection)
      setStoredStageSelection(scopeKey, nextSelection)
    },
    [scopeKey, stages],
  )

  useEffect(() => {
    setStoredSelection(getStoredStageSelection(scopeKey))
  }, [scopeKey])

  return {
    totalValue: summary.totalValue,
    nullOrZeroCount: summary.nullOrZeroCount,
    isLoading,
    selectedStageIds,
    setSelectedStageIds: handleSetSelectedStageIds,
    stageStatistics,
    stages,
  }
}
